import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { LogOut, User, ChevronDown, Settings, Users } from 'lucide-react';
import { loginRequest, graphConfig } from '../../config/auth';
import { useAuth } from '../../hooks/useAuth';

interface UserInfo {
  displayName?: string;
  email?: string;
}

export const Header = () => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [userInfo, setUserInfo] = useState<UserInfo>({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchUserInfo = async () => {
      const account = instance.getActiveAccount();
      if (!account) {
        const accounts = instance.getAllAccounts();
        if (accounts.length > 0) {
          instance.setActiveAccount(accounts[0]);
        }
        return;
      }

      try {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: account
        });

        const graphResponse = await fetch(graphConfig.graphMeEndpoint, {
          headers: {
            Authorization: `Bearer ${response.accessToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (!graphResponse.ok) {
          throw new Error('Failed to fetch user info');
        }

        const data = await graphResponse.json();
        setUserInfo({
          displayName: data.displayName || data.givenName,
          email: data.userPrincipalName || data.mail
        });
      } catch (error) {
        console.error('Error fetching user info:', error);
        setUserInfo({
          displayName: account.name || account.username,
          email: account.username
        });
      }
    };

    fetchUserInfo();
  }, [instance]);

  const handleLogout = async () => {
    console.log('Iniciando logout...');
    setIsDropdownOpen(false);
    
    try {
      // Establecer flag para prevenir re-autenticación
      sessionStorage.setItem('logout_pending', 'true');
      
      // Limpiar todo el storage local
      localStorage.clear();
      
      // Obtener la cuenta activa
      const activeAccount = instance.getActiveAccount();
      
      // Limpiar todas las cuentas del caché
      const accounts = instance.getAllAccounts();
      
      if (accounts && accounts.length > 0) {
        // Remover cada cuenta del caché
        accounts.forEach(account => {
          instance.setActiveAccount(null);
        });
      }
      
      // Limpiar el caché completo de MSAL
      await instance.clearCache();
      
      // Hacer logout solo si hay una cuenta activa
      if (activeAccount) {
        // Usar logout redirect con la cuenta activa
        // Esto cerrará la sesión y redirigirá de vuelta a la app
        const logoutRequest = {
          account: activeAccount,
          postLogoutRedirectUri: window.location.origin
        };
        
        // Usar logoutRedirect
        await instance.logoutRedirect(logoutRequest);
      } else {
        // Si no hay cuenta activa, solo recargar
        window.location.href = '/';
      }
      
    } catch (error) {
      console.error('Error durante logout:', error);
      // En caso de error, forzar limpieza y recarga
      try {
        await instance.clearCache();
      } catch (e) {
        console.error('Error limpiando caché:', e);
      }
      window.location.href = '/';
    }
  };

  // Alternativa: Logout LOCAL sin cerrar sesión en Azure AD
  const handleLocalLogout = async () => {
    console.log('Iniciando logout local...');
    setIsDropdownOpen(false);
    
    try {
      // Establecer flag para prevenir re-autenticación
      sessionStorage.setItem('logout_pending', 'true');
      
      // Limpiar todo el storage
      localStorage.clear();
      
      // Limpiar todas las cookies
      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
      
      // Limpiar el caché de MSAL
      await instance.clearCache();
      
      // Recargar la página
      window.location.href = '/';
      
    } catch (error) {
      console.error('Error durante logout local:', error);
      // Forzar recarga en caso de error
      window.location.href = '/';
    }
  };

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0">
              <img 
                src="https://emin.cl/wp-content/uploads/2025/Logo-Caemin-Grupo-Emin.png" 
                alt="CAEMIN Logo" 
                className="h-12 w-auto"
              />
            </Link>
            <nav className="hidden md:ml-12 md:flex md:space-x-8">
              {isAdmin && (
                <Link 
                  to="/maquinarias" 
                  className="text-gray-700 hover:text-[#004A98] px-3 py-2 text-sm font-medium transition-colors duration-150"
                >
                  Maquinarias
                </Link>
              )}
              <Link 
                to="/reportes" 
                className="text-gray-700 hover:text-[#004A98] px-3 py-2 text-sm font-medium transition-colors duration-150"
              >
                Reportes
              </Link>
              {isAdmin && (
                <Link 
                  to="/admin/users" 
                  className="text-gray-700 hover:text-[#004A98] px-3 py-2 text-sm font-medium transition-colors duration-150"
                >
                  Administración
                </Link>
              )}
            </nav>
          </div>
          
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center space-x-3 text-gray-700 hover:text-gray-900 focus:outline-none p-2 rounded-lg hover:bg-gray-100 transition-colors duration-150"
            >
              <div className="bg-[#004A98] rounded-full p-2">
                <User className="h-5 w-5 text-white" />
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium">
                  {userInfo.displayName || 'Cargando...'}
                </p>
                <p className="text-xs text-gray-500 truncate max-w-[200px]">
                  {userInfo.email}
                </p>
              </div>
              <ChevronDown className="h-4 w-4 text-gray-500" />
            </button>

            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg py-1 z-50 border border-gray-200">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">
                    {userInfo.displayName}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {userInfo.email}
                  </p>
                </div>
                {isAdmin && (
                  <>
                    <button
                      onClick={() => {
                        navigate('/admin/users');
                        setIsDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center group transition-colors duration-150"
                    >
                      <Settings className="h-4 w-4 mr-2 text-gray-400 group-hover:text-gray-500" />
                      Gestión de Usuarios
                    </button>
                    <button
                      onClick={() => {
                        navigate('/admin/operators');
                        setIsDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center group transition-colors duration-150"
                    >
                      <Users className="h-4 w-4 mr-2 text-gray-400 group-hover:text-gray-500" />
                      Gestión de Operadores
                    </button>
                  </>
                )}
                <button
                  onClick={handleLocalLogout}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center group transition-colors duration-150"
                >
                  <LogOut className="h-4 w-4 mr-2 text-gray-400 group-hover:text-gray-500" />
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};